// DCI (Legionella Dashboard) specific flow: This file will be removed once API for DCI (Legionella Dashboard) is unified in BED.
import { ChartData, ChatContent, MixedData } from '../models/chat.model';
import { EvaDciQueryParams, EvaDciResponse } from '../models/eva-request.model';

const EVA_LEGIONELLA_CONTEXT_STORAGE_KEY = 'eva.legionella.context';
const LEGIONELLA_PARSE_ERROR_MESSAGE = 'Sorry, I could not parse the response. Please try again.';

export function getLegionellaEvaDciContext(): EvaDciQueryParams {
  const defaultContext = getDefaultLegionellaEvaDciContext();

  if (typeof window === 'undefined') {
    return defaultContext;
  }

  try {
    const value = sessionStorage.getItem(EVA_LEGIONELLA_CONTEXT_STORAGE_KEY);
    if (!value) {
      return defaultContext;
    }

    const parsed = JSON.parse(value);
    const siteIds = Array.isArray(parsed?.siteIds)
      ? parsed.siteIds
        .map((siteId: unknown) => Number(siteId))
        .filter((siteId: number) => !Number.isNaN(siteId))
      : [];
    const corporateCpId = Number(parsed?.corporateCpId ?? 0);

    return {
      siteIds,
      startDate: typeof parsed?.startDate === 'string' && parsed.startDate.length ? parsed.startDate : defaultContext.startDate,
      endDate: typeof parsed?.endDate === 'string' && parsed.endDate.length ? parsed.endDate : defaultContext.endDate,
      corporateCpId: Number.isNaN(corporateCpId) ? 0 : corporateCpId
    };
  } catch {
    return defaultContext;
  }
}

export function getLegionellaSessionId(response: EvaDciResponse | null): string | null {
  if (!response) {
    return null;
  }

  const sessionId = response.SessionId;
  return typeof sessionId === 'string' && sessionId.trim().length ? sessionId : null;
}

export function getLegionellaResponseContent(response: EvaDciResponse | null): ChatContent {
  const responseText = getLegionellaResponseText(response);

  if (containsLegionellaMermaid(responseText)) {
    return {
      type: 'text',
      data: LEGIONELLA_PARSE_ERROR_MESSAGE
    };
  }

  const hasTable = containsLegionellaTable(responseText);
  const hasChart = containsLegionellaChart(responseText);

  if (!hasTable && !hasChart) {
    return { type: 'text', data: responseText };
  }

  return parseResponseBlocks(responseText);
}

function getDefaultLegionellaEvaDciContext(): EvaDciQueryParams {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 365);

  return {
    siteIds: [],
    startDate: formatDateValue(startDate),
    endDate: formatDateValue(endDate),
    corporateCpId: 0
  };
}

function formatDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getLegionellaResponseText(response: EvaDciResponse | null): string {
  if (!response) {
    return LEGIONELLA_PARSE_ERROR_MESSAGE;
  }

  const responseText = response.Message;

  if (typeof responseText === 'string' && responseText.trim().length) {
    return responseText;
  }

  return LEGIONELLA_PARSE_ERROR_MESSAGE;
}

function containsLegionellaMermaid(message: string): boolean {
  return /```mermaid[\s\S]*?```/i.test(message);
}

function containsLegionellaTable(message: string): boolean {
  return /\|.*\|\s*\n\|\s*[-:]+/i.test(message);
}

function containsLegionellaChart(message: string): boolean {
  return /^```json\s*$/m.test(message);
}

// Parses a markdown response into structured content blocks (text, table, chart).
// Rules:
//   1 block   → that block directly
//   2+ blocks → { type: 'mixed', data: { blocks: [...] } }
function parseResponseBlocks(message: string): ChatContent {
  const normalized = (message || '').replace(/\r\n/g, '\n').trim();
  const blocks = collectBlocks(normalized);

  if (blocks.length === 0) {
    return { type: 'text', data: normalized };
  }

  if (blocks.length === 1) {
    return blocks[0];
  }

  const mixedData: MixedData = { blocks };
  return { type: 'mixed', data: mixedData };
}

// Scans lines and emits alternating text/table/chart ChatContent blocks.
function collectBlocks(normalized: string): ChatContent[] {
  const lines = normalized.split('\n');
  const result: ChatContent[] = [];
  let textBuffer: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    if (isMarkdownTableStart(lines, i)) {
      flushTextBuffer(textBuffer, result);
      textBuffer = [];

      const tableLines: string[] = [];
      while (i < lines.length && looksLikeMarkdownTableLine(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      i--; // outer loop will increment

      const tableBlock = buildTableBlock(tableLines);
      if (tableBlock) {
        result.push(tableBlock);
      }
    } else if (lines[i].trim() === '```json') {
      flushTextBuffer(textBuffer, result);
      textBuffer = [];

      const jsonLines: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== '```') {
        jsonLines.push(lines[i]);
        i++;
      }

      const chartBlock = buildChartBlock(jsonLines.join('\n'));
      if (chartBlock) {
        result.push(chartBlock);
      }
    } else {
      textBuffer.push(lines[i]);
    }
  }

  flushTextBuffer(textBuffer, result);
  return result;
}

function flushTextBuffer(buffer: string[], result: ChatContent[]): void {
  const text = buffer.join('\n').trim();
  if (text) {
    result.push({ type: 'text', data: text });
  }
}

function buildChartBlock(json: string): ChatContent | null {
  try {
    const parsed = JSON.parse(json) as ChartData;
    if (!parsed.chartType || !Array.isArray(parsed.series)) {
      return null;
    }
    return { type: 'chart', data: parsed, metadata: parsed.title ? { title: parsed.title } : undefined };
  } catch {
    return null;
  }
}

function buildTableBlock(tableLines: string[]): ChatContent | null {
  const rows = tableLines
    .map(line => parseMarkdownTableRow(line))
    .filter(row => row.length > 0);

  // rows[0] = headers, rows[1] = separator (---), rows[2+] = body
  if (rows.length < 2) {
    return null;
  }

  const headers = rows[0];
  const bodyRows = rows.slice(2).map(row => {
    const padded = [...row];
    while (padded.length < headers.length) {
      padded.push('');
    }
    return padded.slice(0, headers.length);
  });

  return { type: 'table', data: { headers, rows: bodyRows } };
}

function isMarkdownTableStart(lines: string[], index: number): boolean {
  if (index + 1 >= lines.length) {
    return false;
  }

  const header = lines[index];
  const separator = lines[index + 1];

  return looksLikeMarkdownTableLine(header) && isMarkdownTableSeparator(separator);
}

function looksLikeMarkdownTableLine(line: string): boolean {
  if (!line) {
    return false;
  }

  return line.trim().startsWith('|') && line.trim().endsWith('|');
}

function isMarkdownTableSeparator(line: string): boolean {
  if (!line) {
    return false;
  }

  const separatorPattern = /^\|\s*[:\-]+\s*(\|\s*[:\-]+\s*)+\|\s*$/;
  return separatorPattern.test(line.trim());
}

function parseMarkdownTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());
}
